import * as React from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap'
import {
  PaymentFilter,
  parseTotalTransactionsById,
  PaymentTypeIds,
  IPayment,
  ITaxPaymentLine,
  PaymentLineType,
} from '@getgreenline/homi-shared'
import { Moment } from 'moment'
import { DateRangePicker, SingleDatePicker } from 'react-dates'
import './salesExportModal.scss'
import { toJS } from 'mobx'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { inject, observer } from 'mobx-react'
import { message } from 'antd'
import { parseErrorMsg, capitalizeFirstLetter } from '../../../../utilities/helpers'

import { Track } from '../../../../Track'
import { dayjs } from '../../../../utilities/dayjs'
import { Dayjs } from 'dayjs'
import { convertDayjsToMoment, convertMomentToDayjs } from '../../../../utilities/date'

type PaymentTypes = {
  [K in keyof typeof PaymentTypeIds]: number
}

type PaymentTypeNames = keyof PaymentTypes

type TUniqueTaxTypeObject = Record<string, number>

class Counter {
  cash = 0
  credit = 0
  debit = 0
  other = 0
  etransfer = 0
  merrco = 0
  merrcoDataCandy = 0
  moneris = 0
  global = 0

  addCount = (counterName: PaymentTypeNames, value: number) => {
    this[counterName] = this[counterName] + value
  }

  createColumnName = (paymentType: PaymentTypeNames): string => {
    let humanReadableName = ''

    if (paymentType === 'merrcoDataCandy') {
      humanReadableName = 'Gift card'
    } else if (paymentType === 'etransfer') {
      humanReadableName = 'E-transfer'
    } else {
      humanReadableName = capitalizeFirstLetter(paymentType)
    }

    return `Paid - ${humanReadableName}`
  }

  get allZeroPaymentTypeColumns(): string[] {
    const allZeroPaymentTypes = []

    for (const key of Object.keys(this)) {
      if (typeof this[key] !== 'number') {
        continue
      }
      if (this[key] > 0) {
        continue
      }

      const columnName = this.createColumnName(key as PaymentTypeNames)
      allZeroPaymentTypes.push(columnName)
    }

    return allZeroPaymentTypes
  }
}

enum ExportMode {
  TOTALS = 'Export sales totals',
  LINES = 'Export sales line breakdown',
}

enum DateRangeType {
  TODAY = 'Today',
  THIS_WEEK = 'This week',
  LAST_WEEK = 'Last week',
  THIS_MONTH = 'This month',
  LAST_MONTH = 'Last month',
  CUSTOM_RANGE = 'Custom range',
}

enum FocusedInputType {
  START_DATE = 'startDate',
  END_DATE = 'endDate',
}
enum RangeMode {
  SINGLE = 'Single',
  RANGE = 'Range',
}

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  filter: PaymentFilter
  toggle: () => void
  _export: (fileName: string, json: any) => void
}

interface State {
  selectedDateRangeType: DateRangeType
  selectedExportMode: ExportMode
  focusedRangeInput: FocusedInputType
  paymentExportInProgress: boolean
  rangeMode: RangeMode
}

@inject('currentCompanyStore')
@observer
export class SalesExportModal extends React.Component<Props, State> {
  currentCompanyStore = this.props.currentCompanyStore!
  PAYMENT_LIMIT = 500

  constructor(props: Props) {
    super(props)

    this.state = {
      selectedDateRangeType: DateRangeType.THIS_WEEK,
      selectedExportMode: ExportMode.TOTALS,
      focusedRangeInput: FocusedInputType.START_DATE,
      paymentExportInProgress: false,
      rangeMode: RangeMode.RANGE,
    }
  }

  componentDidMount = () => this.onPreDefinedRangeChange(DateRangeType.THIS_WEEK)

  setDateRangeType = (selectedDateRangeType: DateRangeType) => {
    if (selectedDateRangeType === DateRangeType.CUSTOM_RANGE) {
      const { filter } = this.props
      filter.setStartDate(null)
      filter.setEndDate(null)
    }

    this.setState({ selectedDateRangeType })
  }

  setExportMode = (selectedExportMode: ExportMode) => this.setState({ selectedExportMode })

  setFocusedRangeInput = (focus: FocusedInputType) => this.setState({ focusedRangeInput: focus })

  setPaymentExportInProgress = (paymentExportInProgress: boolean) =>
    this.setState({ paymentExportInProgress })

  setRangeMode = (rangeMode: RangeMode) => this.setState({ rangeMode })

  onPreDefinedRangeChange = (dateRangeType: DateRangeType): void => {
    const { filter } = this.props
    this.setDateRangeType(dateRangeType)

    switch (dateRangeType) {
      case DateRangeType.TODAY:
        const beginningOfToday = dayjs().startOf('day')
        const endingOfToday = dayjs().endOf('day')

        filter.setStartDate(beginningOfToday)
        filter.setEndDate(endingOfToday)
        break
      case DateRangeType.THIS_WEEK:
        const beginningOfThisWeek = dayjs().startOf('week')

        filter.setStartDate(beginningOfThisWeek)
        filter.setEndDate(dayjs())
        break
      case DateRangeType.LAST_WEEK:
        const beginningOfLastWeek = dayjs().subtract(1, 'week').startOf('week')
        const endOfLastWeek = dayjs().subtract(1, 'week').endOf('week')

        filter.setStartDate(beginningOfLastWeek)
        filter.setEndDate(endOfLastWeek)
        break
      case DateRangeType.THIS_MONTH:
        const beginningOfThisMonth = dayjs().startOf('month')

        filter.setStartDate(beginningOfThisMonth)
        filter.setEndDate(dayjs())
        break
      case DateRangeType.LAST_MONTH:
        const beginningOfLastMonth = dayjs().subtract(1, 'month').startOf('month')
        const endOfLastMonth = dayjs().subtract(1, 'month').endOf('month')

        filter.setStartDate(beginningOfLastMonth)
        filter.setEndDate(endOfLastMonth)
        break
      default:
        filter.setStartDate(null)
        filter.setEndDate(null)
    }
  }

  onCustomDateChange = (date: Moment | null): void => {
    const { filter } = this.props
    const startDate = date ? date.startOf('day') : null
    // clone() is used to work around Moment object's mutability
    const endDate = date ? date.clone().endOf('day') : null

    filter.setStartDate(convertMomentToDayjs(startDate))
    filter.setEndDate(convertMomentToDayjs(endDate))
  }

  onCustomRangeChange = (paramsStartDate: Moment | null, paramsEndDate: Moment | null): void => {
    const { filter } = this.props

    const startDate = paramsStartDate ? paramsStartDate.startOf('day') : null
    const endDate = paramsEndDate ? paramsEndDate.endOf('day') : null

    filter.setStartDate(convertMomentToDayjs(startDate))
    filter.setEndDate(convertMomentToDayjs(endDate))
  }

  getLineTotalTransaction = (
    counters: Counter,
    paymentTypeId: PaymentTypeIds,
    payment: IPayment,
  ) => {
    const lineTotal = parseTotalTransactionsById(paymentTypeId, payment.transactions) / 100
    const paymentTypeName = PaymentTypeIds[paymentTypeId] as PaymentTypeNames

    counters.addCount(paymentTypeName, lineTotal)

    return lineTotal
  }

  getTaxObject = (taxPaymentLines: ITaxPaymentLine[], uniqueTaxObject: TUniqueTaxTypeObject) => {
    taxPaymentLines.forEach((taxLine) => {
      if (uniqueTaxObject.hasOwnProperty(taxLine.taxName)) {
        uniqueTaxObject[taxLine.taxName] += taxLine.amount / 100
      }
    })

    // Only show 2 decimals
    Object.keys(uniqueTaxObject).map((key) => {
      uniqueTaxObject[key] = Number(uniqueTaxObject[key].toFixed(2))
    })

    return uniqueTaxObject
  }

  getUniqueTaxNames = (payments: IPayment[]) => {
    const taxNames = payments.reduce((taxNameAccumulator: string[], currPayment) => {
      const taxNameArray = currPayment.taxPaymentLines.reduce(
        (prev: string[], curr) => [...prev, curr.taxName],
        [],
      )
      return [...taxNameAccumulator, ...taxNameArray]
    }, [])

    return taxNames.filter((value, index, self) => self.indexOf(value) === index)
  }

  getUniquePaymentTypes = (payment: IPayment) => {
    const paymentTypes = payment.transactions.map((transaction) => transaction.paymentType)
    return [...new Set(paymentTypes)]
  }

  createSalesTotals = (payments: IPayment[], paymentsJSON: any[] = []) => {
    // Get unique tax names within the report
    const uniqueTaxNames = this.getUniqueTaxNames(payments)
    const uniqueTaxTypeObject: TUniqueTaxTypeObject = {}

    const counters = new Counter()

    payments.forEach((payment) => {
      // Sum of discounts
      const totalDiscountAmount = payment.paymentLines.reduce(
        (prev, acc) => prev + Number(acc.discountPrice.toFixed(2)),
        0,
      )

      const paymentDiscounts = payment.paymentLines.map((pl) => pl.discountMessage).filter((m) => m)

      const cartDiscounts = payment.paymentLines
        .filter((pl) => pl.type === PaymentLineType.DISCOUNT)
        .map((d) => d.productName)

      const discountNames = Array.from(new Set([...paymentDiscounts, ...cartDiscounts])).join(', ')

      // Sum of deposit fees
      const totalDepositFee = payment.paymentLines.reduce((prev, curr) => {
        const deposit = curr.depositFee || 0
        return prev + Number(deposit.toFixed(2))
      }, 0)

      /**
       * Gross total indicates the sum of charge purely from product selling
       * It excludes tax, discounts, and deposit fees
       */
      const grossTotal =
        payment.total - payment.tax - totalDepositFee + Math.abs(totalDiscountAmount)

      // Gross total INCLUDING discounts
      const netTotal = grossTotal - Math.abs(totalDiscountAmount)

      // Reset tax amount to 0 for every new payment
      uniqueTaxNames.forEach((taxName) => {
        uniqueTaxTypeObject[taxName] = 0
      })

      const [
        cash,
        credit,
        debit,
        other,
        etransfer,
        merrco,
        merrcoDataCandy,
        moneris,
        global,
        online,
      ] = [
        this.getLineTotalTransaction(counters, PaymentTypeIds.cash, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.credit, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.debit, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.other, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.etransfer, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.merrco, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.merrcoDataCandy, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.moneris, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.global, payment),
        this.getLineTotalTransaction(counters, PaymentTypeIds.online, payment),
      ]

      const taxes =
        Object.keys(uniqueTaxTypeObject).length === 0
          ? {}
          : this.getTaxObject(payment.taxPaymentLines, uniqueTaxTypeObject)

      const authTransaction = payment.transactions.find((t) => t.terminalInvoiceId)

      paymentsJSON.push({
        'Sale ID': payment.customId,
        'Create Date': payment.createDate
          ? dayjs(payment.createDate).format('YYYY-MM-DD HH:mm:ss')
          : '-',
        'Complete Date': payment.completeDate
          ? dayjs(payment.completeDate).format('YYYY-MM-DD HH:mm:ss')
          : '-',
        Source: payment.externalSourceName || 'Greenline',
        'Source Order ID': payment.externalOrderId,
        Status: payment.status,
        'Payment Types': this.getUniquePaymentTypes(payment).join(', '),
        Location: payment.locationName,
        Employee: payment.employeeName,
        Customer: payment.customerName,
        'Device ID': payment.deviceName,
        'Shift ID': payment.shiftId,
        'Auth #': authTransaction ? authTransaction.terminalInvoiceId : null,
        'Paid - Cash': cash,
        'Paid - Credit': credit,
        'Paid - Debit': debit,
        'Paid - Merrco': merrco,
        'Paid - Gift card': merrcoDataCandy,
        'Paid - Online': online,
        'Paid - Moneris': moneris,
        'Paid - Other': other,
        'Paid - E-transfer': etransfer,
        'Paid - Global': global,
        'Gross Total': grossTotal / 100,
        'Total Discount': totalDiscountAmount / 100,
        'Discount Name': discountNames || '',
        'Net Total': netTotal / 100,
        ...taxes,
        Tax: payment.tax / 100,
        'Deposit Fee': totalDepositFee / 100,
        Total: payment.total / 100,
        'Penny Rounding': payment.pennyAdjustment / 100,
        Notes: payment.notes,
      })
    })

    if (counters.allZeroPaymentTypeColumns.length > 0) {
      paymentsJSON.forEach((paymentJSON) => {
        counters.allZeroPaymentTypeColumns.forEach((paymentType) => {
          delete paymentJSON[paymentType]
        })
      })
    }

    return paymentsJSON
  }

  createSalesLines = (payments: IPayment[], paymentLinesJSON: any[] = []) => {
    const uniqueTaxNames = this.getUniqueTaxNames(payments)
    const uniqueTaxTypeObject: TUniqueTaxTypeObject = {}

    payments.forEach((payment) => {
      const cartDiscounts = payment.paymentLines
        .filter((pl) => pl.type === PaymentLineType.DISCOUNT)
        .map((d) => d.productName)

      for (const paymentLine of payment.paymentLines) {
        if (paymentLine.productId === null || paymentLine.productId.length === 0) {
          continue
        }

        const targetTaxPaymentLines = payment.taxPaymentLines.filter((taxLine) => {
          return taxLine.paymentLineId !== null && taxLine.paymentLineId === paymentLine.id
        })

        const taxSum = targetTaxPaymentLines.reduce((prev, acc) => prev + acc.amount, 0)

        // Reset tax amount to 0 for every new payment
        uniqueTaxNames.forEach((taxName) => {
          uniqueTaxTypeObject[taxName] = 0
        })

        const taxes =
          Object.keys(uniqueTaxTypeObject).length === 0
            ? {}
            : this.getTaxObject(targetTaxPaymentLines, uniqueTaxTypeObject)

        const totalDiscount = paymentLine.discountPrice + paymentLine.cartDiscountPrice

        const discountNames = Array.from(
          new Set(
            paymentLine.discountMessage
              ? [paymentLine.discountMessage, ...cartDiscounts]
              : cartDiscounts,
          ),
        ).join(', ')

        const line = {
          'Sale ID': payment.customId,
          'Create Date': payment.createDate
            ? dayjs(payment.createDate).format('YYYY-MM-DD HH:mm:ss')
            : '-',
          'Complete Date': payment.completeDate
            ? dayjs(payment.completeDate).format('YYYY-MM-DD HH:mm:ss')
            : '-',
          Source: payment.externalSourceName || 'Greenline',
          'Source Order ID': payment.externalOrderId,
          Status: payment.status,
          'Payment Types': this.getUniquePaymentTypes(payment).join(', '),
          Location: payment.locationName,
          Employee: payment.employeeName,
          Customer: payment.customerName,
          'Device ID': payment.deviceName,
          'Shift ID': payment.shiftId,
          'Product ID': paymentLine.productId,
          'Product SKU': paymentLine.sku,
          'Product Name': paymentLine.productName,
          'Lot #': paymentLine.batchNumber,
          'List Price': paymentLine.price / 100,
          'Total Discount': totalDiscount / 100,
          'Discount Name': discountNames || '',
          'Sold at Price': (paymentLine.price + totalDiscount) / 100,
          Quantity: paymentLine.inventorySubtracted,
          Cost: paymentLine.purchaseCost / 100,
          ...taxes,
          Tax: targetTaxPaymentLines.length === 0 ? '' : (taxSum / 100).toFixed(2),
        }

        paymentLinesJSON.push(line)
      }
    })

    return paymentLinesJSON
  }

  exportPayment = async () => {
    const { filter, _export } = this.props
    const { selectedExportMode } = this.state

    if (!filter.startDate || !filter.endDate) {
      return
    }

    const filtersCopy = JSON.parse(JSON.stringify(toJS(filter)))
    // Cloning doesn't handle moment objects well, will have to be recast
    filtersCopy.startDate = filtersCopy.startDate ? dayjs(filtersCopy.startDate) : undefined
    filtersCopy.endDate = filtersCopy.endDate ? dayjs(filtersCopy.endDate) : undefined
    const newFilter = Object.assign(
      new PaymentFilter(this.currentCompanyStore.company!.id, 0, 0),
      filtersCopy,
    )

    newFilter.offset = 0
    newFilter.limit = this.PAYMENT_LIMIT

    this.setPaymentExportInProgress(true)
    if (!(filter.locationIds && filter.locationIds.length > 0)) return

    try {
      const paginatedPayments = await this.currentCompanyStore.getLocationPayments(
        filter.locationIds[0],
        newFilter,
      )
      Track.track('dashboard:sales_exported', newFilter)

      let payments = [...paginatedPayments.payments]

      if (paginatedPayments.total > this.PAYMENT_LIMIT) {
        for (
          let offset = this.PAYMENT_LIMIT;
          offset < paginatedPayments.total;
          offset = offset + this.PAYMENT_LIMIT
        ) {
          newFilter.offset = offset
          const paginatedPayments = await this.currentCompanyStore.getLocationPayments(
            filter.locationIds[0],
            newFilter,
          )
          payments = [...payments, ...paginatedPayments.payments]
        }
      }

      const paymentsJSON =
        selectedExportMode === ExportMode.TOTALS
          ? this.createSalesTotals(payments)
          : this.createSalesLines(payments)

      const filename = `sales ${filter.startDate.format('LLL')}-${filter.endDate.format('LLL')}`

      _export(filename, paymentsJSON)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while exporting sales records')
    } finally {
      this.setPaymentExportInProgress(false)
    }
  }

  formatDate(date: Dayjs | null): string | undefined {
    return date !== null ? date.format('ll') : undefined
  }

  get timeDiff(): number | undefined {
    const { filter } = this.props

    return filter.startDate !== null && filter.endDate !== null
      ? dayjs(filter.endDate).diff(filter.startDate, 'month', true)
      : undefined
  }

  get durationExceeded(): boolean | undefined {
    return this.timeDiff === undefined ? undefined : this.timeDiff > 1
  }

  get isButtonDisabled(): boolean {
    if (this.state.paymentExportInProgress || this.durationExceeded === undefined) {
      return true
    }

    return this.durationExceeded
  }

  get exportButtonLabel(): string {
    // Either startDate or endDate is null
    if (this.durationExceeded === undefined) {
      return 'Select date range'
    }

    if (this.durationExceeded) {
      return 'Exceeded maximum 1 month'
    } else {
      return `${this.isButtonDisabled ? 'Downloading' : 'Export'} CSV`
    }
  }

  render() {
    const { filter, toggle } = this.props
    const { selectedDateRangeType, selectedExportMode, focusedRangeInput, rangeMode } = this.state

    return (
      <Modal
        isOpen={true}
        toggle={toggle}
        autoFocus={false}
        size={selectedDateRangeType === DateRangeType.CUSTOM_RANGE ? 'xl' : 'md'}
      >
        <ModalHeader toggle={toggle}>Export sales</ModalHeader>

        <ModalBody>
          <section className='export-sales-section'>
            <header className='text-muted export-sales-header'>Fields</header>
            <main>
              {Object.keys(ExportMode).map((modeKey, index) => {
                const exportMode = ExportMode[modeKey]
                const inputId = `radio-${exportMode}-${index}`

                return (
                  <div key={modeKey}>
                    <input
                      id={inputId}
                      type='radio'
                      checked={selectedExportMode === exportMode}
                      className='cursor-pointer d-inline mr-2'
                      onClick={() => this.setExportMode(exportMode)}
                    />
                    <label className='cursor-pointer export-sales-label' htmlFor={inputId}>
                      {exportMode}
                    </label>
                  </div>
                )
              })}
            </main>
          </section>

          <section className='export-sales-section'>
            <header className='text-muted export-sales-header'>Dates (maximum 1 month)</header>
            <main>
              {Object.keys(DateRangeType).map((typeKey, index) => {
                const dateRangeType = DateRangeType[typeKey]
                const inputId = `radio-${dateRangeType}-${index}`

                return (
                  <div key={typeKey}>
                    <input
                      id={inputId}
                      type='radio'
                      checked={selectedDateRangeType === dateRangeType}
                      className='cursor-pointer d-inline mr-2'
                      onClick={() => this.onPreDefinedRangeChange(dateRangeType)}
                    />
                    <label className='cursor-pointer export-sales-label' htmlFor={inputId}>
                      {dateRangeType}
                    </label>
                  </div>
                )
              })}
            </main>
          </section>

          {selectedDateRangeType === DateRangeType.CUSTOM_RANGE && (
            <section>
              <UncontrolledDropdown className='mr-2 mb-3'>
                <DropdownToggle caret color='light'>
                  {rangeMode}
                </DropdownToggle>

                <DropdownMenu>
                  <DropdownItem onClick={() => this.setRangeMode(RangeMode.SINGLE)}>
                    Single date
                  </DropdownItem>

                  <DropdownItem onClick={() => this.setRangeMode(RangeMode.RANGE)}>
                    Date range
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              {rangeMode === RangeMode.SINGLE ? (
                <SingleDatePicker
                  id='single-date-picker-export-sales'
                  date={convertDayjsToMoment(filter.startDate) || null}
                  showClearDate={true}
                  isOutsideRange={(day: Moment) => false}
                  numberOfMonths={2}
                  onDateChange={(date) => this.onCustomDateChange(date)}
                  focused={true}
                  onFocusChange={() => {}}
                />
              ) : (
                <DateRangePicker
                  startDateId='sale-start-date'
                  endDateId='sale-end-date'
                  showClearDates={true}
                  isOutsideRange={() => false}
                  startDate={convertDayjsToMoment(filter.startDate)}
                  endDate={convertDayjsToMoment(filter.endDate)}
                  onDatesChange={({ startDate, endDate }) =>
                    this.onCustomRangeChange(startDate, endDate)
                  }
                  focusedInput={focusedRangeInput}
                  onFocusChange={(focus) => {
                    const newFocusedInput =
                      focus === null ? FocusedInputType.START_DATE : (focus as FocusedInputType)

                    this.setFocusedRangeInput(newFocusedInput)
                  }}
                />
              )}
            </section>
          )}

          <button
            className={`btn btn-light mt-3 w-100${
              this.isButtonDisabled ? ' cursor-not-allowed' : ''
            }`}
            onClick={this.exportPayment}
            disabled={this.isButtonDisabled}
          >
            <div>{this.exportButtonLabel}</div>

            {filter.startDate && filter.endDate && (
              <small style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                {`${this.formatDate(filter.startDate)} ~ ${this.formatDate(filter.endDate)}`}
              </small>
            )}
          </button>
        </ModalBody>
      </Modal>
    )
  }
}
